<template>
  <ElForm label-width="auto" class="w-full flex flex-col gap-4">
    <ElFormItem
      label="Vertrag"
      for="Vertrag"
      :label-position="isMobile ? 'top' : 'left'"
    >
      <div class="w-full md:w-80 md:ml-auto">
        <ElSelectV2
          id="Vertrag"
          :options="contracts"
          :model-value="contractId"
          :loading="loading"
          placeholder=""
          clearable
          filterable
          :item-height="48"
          popper-class="el-select__popper--multiline"
          class="w-full"
          @update:model-value="$emit('update:contractId', $event)"
        >
          <template #default="{ item }">
            <div class="flex flex-col" :class="{'text-muted': item.inactive}">
              <span class="truncate">{{ item.label }} {{ item.inactive ? '(inaktiv)' : '' }}</span>
              <span class="truncate text-xs">{{ item.number ? item.number : 'Keine Vertragsnummer' }} </span>
            </div>
          </template>
        </ElSelectV2>
      </div>
    </ElFormItem>

    <ElFormItem
      label="Typ"
      for="Typ"
      :label-position="isMobile ? 'top' : 'left'"
    >
      <div class="w-full md:w-80 md:ml-auto">
        <ElSelect
          id="Typ"
          :model-value="documentTypeId"
          placeholder=""
          clearable
          filterable
          class="w-full"
          @update:model-value="$emit('update:documentTypeId', $event)"
        >
          <ElOption
            v-for="option in docTypes"
            :key="option.value"
            :label="option.label"
            :value="option.value"
          />
        </ElSelect>
      </div>
    </ElFormItem>

    <ElFormItem
      label="Gesellschaft"
      for="Gesellschaft"
      :label-position="isMobile ? 'top' : 'left'"
    >
      <div class="w-full md:w-80 md:ml-auto">
        <ElSelect
          id="Gesellschaft"
          :model-value="insuranceCompanyId"
          placeholder=""
          clearable
          filterable
          class="w-full"
          @update:model-value="$emit('update:insuranceCompanyId', $event)"
        >
          <ElOption
            v-for="option in companies"
            :key="option.value"
            :label="option.label"
            :value="option.value"
          />
        </ElSelect>
      </div>
    </ElFormItem>

    <ElFormItem
      label="Sparte"
      for="Sparte"
      :label-position="isMobile ? 'top' : 'left'"
    >
      <div class="w-full md:w-80 md:ml-auto">
        <ElSelect
          id="Sparte"
          :model-value="productComboId"
          placeholder=""
          clearable
          filterable
          class="w-full"
          @update:model-value="$emit('update:productComboId', $event)"
        >
          <ElOption
            v-for="option in sparten"
            :key="option.id"
            :label="option.display_name"
            :value="option.id"
          >
            {{ option.display_name }}
            <span
              v-if="!!option.type"
              class="inline-block rounded-sm px-[0.4em] py-[0.25em] text-[75%] font-bold leading-none text-center align-baseline whitespace-nowrap text-secondary"
            >
              {{ option.type }}
            </span>
          </ElOption>
        </ElSelect>
      </div>
    </ElFormItem>


    <ElFormItem
      label="Notiz für den Makler"
      for="Notiz"
      :label-position="isMobile ? 'top' : 'left'"
    >
      <div class="w-full md:flex justify-end">
        <ElInput
          id="Notiz"
          type="textarea"
          :model-value="note ?? ''"
          :placeholder="`${duzen('Hinterlassen Sie', 'Hinterlasse')} eine Notiz zur Vertragsanlage ...`"
          :rows="3"
          class="w-full md:w-80"
          @update:model-value="$emit('update:note', $event)"
        />
      </div>
    </ElFormItem>
  </ElForm>
</template>

<script setup lang="ts">
import { ElForm, ElFormItem, ElInput, ElOption, ElSelect, ElSelectV2 } from 'element-plus';

import type { ContractType, Sparte } from '@/api/common/types';
import type { SelectOption } from '@/application/components/propTypes';
import { useDetectMobile } from "@/application/composables/detectMobile";
import { useBaseDataStore } from '@/user/store/baseDataStore.ts';

interface Props {
  contracts: ContractType[];
  docTypes: SelectOption[];
  companies: SelectOption[];
  sparten: Sparte[];
  contractId?: number | null;
  documentTypeId?: number | null;
  insuranceCompanyId?: number | null;
  productComboId?: number | null;
  note?: string | null;
  loading?: boolean;
}

withDefaults(defineProps<Props>(), {
  contractId: null,
  documentTypeId: null,
  insuranceCompanyId: null,
  productComboId: null,
  note: null,
  loading: false,
});

defineEmits([
  'update:contractId',
  'update:documentTypeId',
  'update:insuranceCompanyId',
  'update:productComboId',
  'update:note',
]);

const { isMobile } = useDetectMobile();
const { duzen } = useBaseDataStore();
</script>
